import { SceneDataProps } from '@/types/sceneProps'
import { MusicalGenresDataProps } from '@/types/musicalGenresProps'
import { Input } from '../form/Input'
import { useForm } from 'react-hook-form'
import { Button } from '../Button'
import { useEffect, useMemo, useState } from 'react'

import { useDispatch } from 'react-redux'
import { handleSetScenes } from '@/storage/modules/scenes/reducer'
import { handleSetMusicalGenres } from '@/storage/modules/musicalGenres/reducer'
import { useToast } from '@/hooks/useToast'
import { useModal } from '@/hooks/useModal'
import { api } from '@/services/api'
import { notifyScenesUpdated } from '@/services/scenesEvents'
import {
  type PaginatedResponseProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'
import { IoAdd, IoClose } from 'react-icons/io5'
import colors from 'tailwindcss/colors'

interface FormSceneProps {
  isExist?: SceneDataProps
}

interface SceneFormData {
  title: string
}

function extractPayload<T>(payload: T | { data: T }): T {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'data' in payload &&
    payload.data !== undefined
  ) {
    return payload.data
  }

  return payload as T
}

export function FormScene({ isExist }: FormSceneProps) {
  const { register, handleSubmit, setValue } = useForm<SceneFormData>()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingGenres, setIsLoadingGenres] = useState(false)
  const [includedGenres, setIncludedGenres] = useState<
    MusicalGenresDataProps[]
  >([])
  const [availableGenres, setAvailableGenres] = useState<
    MusicalGenresDataProps[]
  >([])
  const [initialIncludedGenreIds, setInitialIncludedGenreIds] = useState<
    string[]
  >([])

  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { closeModal } = useModal()

  const refreshScenes = async () => {
    const responseScenes = await api
      .get('/scenes', {
        params: {
          page: 1,
        },
      })
      .then((res) => res.data.data as SceneDataProps[])

    dispatch(handleSetScenes({ scenes: responseScenes }))
    notifyScenesUpdated()
  }

  const refreshGenres = async () => {
    const responseGenres = await api
      .get('/genres', {
        params: {
          page: 1,
        },
      })
      .then((res) => res.data.data as MusicalGenresDataProps[])

    dispatch(handleSetMusicalGenres({ musicalGenres: responseGenres }))
  }

  const fetchAllGenres = async () => {
    const firstPageResponse = await api.get<
      PaginatedResponseProps<MusicalGenresDataProps>
    >('/genres', {
      params: {
        page: 1,
      },
    })

    const firstPageGenres = firstPageResponse.data.data
    const pagination = normalizePaginationMeta(
      firstPageResponse.data.meta,
      firstPageGenres.length,
    )

    if (pagination.lastPage <= 1) {
      return firstPageGenres
    }

    const nextPagesResponses = await Promise.all(
      Array.from({ length: pagination.lastPage - 1 }, (_, index) =>
        api.get<PaginatedResponseProps<MusicalGenresDataProps>>('/genres', {
          params: {
            page: index + 2,
          },
        }),
      ),
    )

    return firstPageGenres.concat(
      nextPagesResponses.flatMap((response) => response.data.data),
    )
  }

  const syncSceneGenres = async (sceneId: string) => {
    const currentIncludedGenreIds = includedGenres.map((genre) => genre.id)
    const removedGenreIds = initialIncludedGenreIds.filter(
      (genreId) => !currentIncludedGenreIds.includes(genreId),
    )

    await Promise.all([
      ...includedGenres.map((genre) =>
        api.patch(`/genres/${genre.id}`, {
          title: genre.title,
          sceneId,
        }),
      ),
      ...removedGenreIds.map((genreId) =>
        api.patch(`/genres/${genreId}`, {
          sceneId: null,
        }),
      ),
    ])
  }

  const handleSaveNewScene = async (data: SceneFormData) => {
    const response = await api.post('/scenes', data)
    const createdScene = extractPayload(
      response.data as SceneDataProps | { data: SceneDataProps },
    )

    await syncSceneGenres(createdScene.id)

    showToast('Scene added successfully', {
      type: 'success',
      theme: 'light',
    })

    await Promise.all([refreshScenes(), refreshGenres()])
  }

  const handleEditScene = async (data: SceneFormData, id: string) => {
    await api.patch(`/scenes/${id}`, data)
    await syncSceneGenres(id)

    showToast('Scene edited successfully', {
      type: 'success',
      theme: 'light',
    })

    await Promise.all([refreshScenes(), refreshGenres()])
  }

  const handleIncludeGenre = (genreId: string) => {
    const selectedGenre = availableGenres.find((genre) => genre.id === genreId)

    if (!selectedGenre) {
      return
    }

    setAvailableGenres((prev) => prev.filter((genre) => genre.id !== genreId))
    setIncludedGenres((prev) =>
      [...prev, selectedGenre].sort((a, b) => a.title.localeCompare(b.title)),
    )
  }

  const handleExcludeGenre = (genreId: string) => {
    const selectedGenre = includedGenres.find((genre) => genre.id === genreId)

    if (!selectedGenre) {
      return
    }

    setIncludedGenres((prev) => prev.filter((genre) => genre.id !== genreId))
    setAvailableGenres((prev) =>
      [...prev, { ...selectedGenre, sceneId: null }].sort((a, b) =>
        a.title.localeCompare(b.title),
      ),
    )
  }

  const onSubmit = async (data: SceneFormData) => {
    try {
      setIsLoading(true)

      if (isExist) {
        await handleEditScene(data, isExist.id)
      } else {
        await handleSaveNewScene(data)
      }

      closeModal()
    } catch (err) {
      const errResponse =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof err.response === 'object' &&
        err.response !== null &&
        'data' in err.response &&
        typeof err.response.data === 'object' &&
        err.response.data !== null
          ? err.response.data
          : null

      const errMessage =
        errResponse && 'message' in errResponse
          ? String(errResponse.message)
          : 'Error saving scene'

      showToast(errMessage, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const availableGenresTitle = useMemo(() => {
    return isExist ? 'Genres not included' : 'Available genres'
  }, [isExist])

  useEffect(() => {
    if (isExist) {
      setValue('title', isExist.title)
    }
  }, [isExist, setValue])

  useEffect(() => {
    let isMounted = true

    const loadGenres = async () => {
      try {
        setIsLoadingGenres(true)

        const genres = await fetchAllGenres()

        if (!isMounted) {
          return
        }

        const included = genres
          .filter((genre) => genre.sceneId === isExist?.id)
          .sort((a, b) => a.title.localeCompare(b.title))
        const available = genres
          .filter((genre) => !genre.sceneId || genre.sceneId === isExist?.id)
          .filter((genre) => genre.sceneId !== isExist?.id)
          .sort((a, b) => a.title.localeCompare(b.title))

        setIncludedGenres(included)
        setAvailableGenres(available)
        setInitialIncludedGenreIds(included.map((genre) => genre.id))
      } catch (error) {
        if (!isMounted) {
          return
        }

        showToast('Error fetching genres for scene', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        if (isMounted) {
          setIsLoadingGenres(false)
        }
      }
    }

    loadGenres()

    return () => {
      isMounted = false
    }
  }, [isExist?.id, showToast])

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <Input
          placeholder="Digit new scene"
          label="Scene"
          name="title"
          register={register}
          required
        />

        <div className="grid grid-cols-2 gap-5 md:grid-cols-1">
          <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className="text-sm font-bold text-gray-700">
                Included genres
              </h4>
              <span className="text-xs font-semibold text-gray-400">
                {includedGenres.length}
              </span>
            </div>

            <div className="flex max-h-[280px] flex-col gap-2 overflow-y-auto pr-1">
              {!isLoadingGenres && includedGenres.length === 0 && (
                <p className="text-sm text-gray-400">
                  No genres included in this scene yet.
                </p>
              )}

              {includedGenres.map((genre) => (
                <div
                  key={genre.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2"
                >
                  <span className="min-w-0 truncate text-sm font-medium text-gray-600">
                    {genre.title}
                  </span>
                  <button
                    type="button"
                    title="Remove from scene"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 transition hover:bg-red-200"
                    onClick={() => handleExcludeGenre(genre.id)}
                  >
                    <IoClose size={14} color={colors.red[600]} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className="text-sm font-bold text-gray-700">
                {availableGenresTitle}
              </h4>
              <span className="text-xs font-semibold text-gray-400">
                {availableGenres.length}
              </span>
            </div>

            <div className="flex max-h-[280px] flex-col gap-2 overflow-y-auto pr-1">
              {!isLoadingGenres && availableGenres.length === 0 && (
                <p className="text-sm text-gray-400">
                  No genres available to include.
                </p>
              )}

              {availableGenres.map((genre) => (
                <div
                  key={genre.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2"
                >
                  <span className="min-w-0 truncate text-sm font-medium text-gray-600">
                    {genre.title}
                  </span>
                  <button
                    type="button"
                    title="Include in scene"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-purple-100 transition hover:bg-purple-200"
                    onClick={() => handleIncludeGenre(genre.id)}
                  >
                    <IoAdd size={14} color={colors.purple[600]} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Button
          title={isExist ? 'Edit' : 'Save'}
          variant="purple"
          isLoading={isLoading || isLoadingGenres}
        />
      </form>
    </div>
  )
}
